import { useEffect } from 'react';
import type { Flow } from '../../contract/flow';
import type { FlowSubscription } from '../../contract/subscription';
import { isServer } from '../../utils/isServer';
import { skipToken, type SkipToken } from '../skipToken';

interface RenderSubscriptionEntry {
    subscription: FlowSubscription;
    timeoutId: ReturnType<typeof setTimeout> | null;
    // Момент, когда подписку нужно освободить
    releaseAt: number;
}

// Можно безопасно хранить подписки в глобальной переменной, так как на севрере они не создаются
const renderSubscriptions = new Map<Flow<unknown>, RenderSubscriptionEntry>();

/**
 * Устанавливает таймер на остаток до `releaseAt`.
 * Благодаря этому повторные acquire не создают новых setTimeout/clearTimeout — только сдвигают число `releaseAt`.
 */
function armRenderTimer(flow: Flow<unknown>, entry: RenderSubscriptionEntry): void {
    const remaining = entry.releaseAt - Date.now();
    entry.timeoutId = setTimeout(
        () => {
            entry.timeoutId = null;
            if (entry.releaseAt - Date.now() > 0) {
                armRenderTimer(flow, entry);
                return;
            }
            releaseRenderSubscription(flow);
        },
        Math.max(0, remaining)
    );
}

/**
 * @internal Экспортируется только для тестов.
 */
export function acquireRenderSubscription(flow: Flow<unknown>, timeoutMs: number): void {
    const existing = renderSubscriptions.get(flow);
    if (existing) {
        existing.releaseAt = Math.max(existing.releaseAt, Date.now() + timeoutMs);
        if (existing.timeoutId === null) {
            armRenderTimer(flow, existing);
        }
        return;
    }

    const subscription = flow.subscribe(() => {});
    const entry: RenderSubscriptionEntry = {
        subscription,
        timeoutId: null,
        releaseAt: Date.now() + timeoutMs,
    };
    renderSubscriptions.set(flow, entry);
    armRenderTimer(flow, entry);
}

export function cleanupRenderSubscriptions(): void {
    for (const entry of renderSubscriptions.values()) {
        if (entry.timeoutId !== null) {
            clearTimeout(entry.timeoutId);
        }
        entry.subscription.unsubscribe();
    }
    renderSubscriptions.clear();
}

function releaseRenderSubscription(flow: Flow<unknown>): void {
    const entry = renderSubscriptions.get(flow);
    if (!entry) {
        return;
    }

    if (entry.timeoutId !== null) {
        clearTimeout(entry.timeoutId);
    }
    entry.subscription.unsubscribe();
    renderSubscriptions.delete(flow);
}

/**
 * Создает временную подписку на flow во время render-фазы React. Парный хук для освобождения —
 * {@link useRenderSubscriptionCleanup} — должен быть вызван ПОСЛЕ постоянной подписки
 * (например, после `useSyncExternalStore`).
 *
 * Зачем нужен:
 * `useSyncExternalStore` (внутри `useFlow`) создаёт подписку только на этапе commit (mount).
 * Когда срабатывает Suspense (в текущем компоненте или где-нибудь рядом в react дереве),
 * компонент приостанавливается, подписка не создаётся, и поток теряет подписчиков.
 * При отсутсвии подписчиков поток отписывается от источников.
 * Например, RTK Query удаляет кеш и данные теряются (при `keepUnusedDataFor: 0`).
 *
 * Как работает:
 * - Render-фаза (синхронно): создаёт подписку `flow.subscribe(() => {})` и сохраняет её
 *   в модульном `Map` (не в стейте компонента, т.к. стейт компонента теряется при Suspense).
 * - Safety timeout: страховка для abandoned renders в concurrent mode, когда `useEffect`
 *   из {@link useRenderSubscriptionCleanup} никогда не вызывается. По истечении таймаута
 *   temp-подписка освобождается, и при следующем render данные будут перезагружены заново.
 *
 * @param flow - Flow для подписки или `skipToken` для пропуска
 * @param timeoutMs - Таймаут в миллисекундах для safety-освобождения temp-подписки
 *
 * @internal
 */
export function useRenderSubscription(flow: Flow<unknown> | SkipToken, timeoutMs: number): void {
    if (flow !== skipToken && timeoutMs > 0 && !isServer()) {
        acquireRenderSubscription(flow, timeoutMs);
    }
}

/**
 * Освобождает временную подписку, созданную через {@link useRenderSubscription}, на commit
 * компонента и при unmount.
 *
 * Вызывать ОБЯЗАТЕЛЬНО после `useSyncExternalStore` (или иного механизма, создающего постоянную
 * подписку) — иначе release useEffect отработает раньше, чем будет создана постоянная подписка,
 * и между ними появится зазор с нулем подписчиков, в котором источник может выкинуть данные.
 *
 * Если соответствующего acquire не было (например, useRenderSubscription был пропущен из-за
 * `skipToken` или `timeoutMs=0`), вызов безопасен — `releaseRenderSubscription` для отсутствующей
 * записи в Map является no-op.
 *
 * @param flow - Flow подписки или `skipToken`
 *
 * @internal
 */
export function useRenderSubscriptionCleanup(flow: Flow<unknown> | SkipToken): void {
    useEffect(() => {
        if (flow !== skipToken) {
            releaseRenderSubscription(flow);
        }

        return () => {
            if (flow !== skipToken) {
                releaseRenderSubscription(flow);
            }
        };
    });
}
